import { useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { getPendingInvitesForEmail, acceptInvite } from '@/integrations/supabase/inviteService';
import { mergeManualPlayer } from '@/integrations/supabase/manualPlayerService';

/**
 * Processes pending invites on login. Same pattern as useLoneWolf:
 * runs once per session via ref guard.
 */
export const useProcessInvites = () => {
  const { user } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (!user || hasProcessed.current) return;
    hasProcessed.current = true;

    const processInvites = async () => {
      try {
        const invites = await getPendingInvitesForEmail();
        if (invites.length === 0) return;

        for (const invite of invites) {
          try {
            // Create friendship (inviter → current user, auto-accepted)
            await supabase
              .from('friendships')
              .upsert(
                {
                  user_id: invite.inviter_id,
                  friend_id: user.id,
                  status: 'accepted',
                },
                { onConflict: 'user_id,friend_id', ignoreDuplicates: false }
              );

            // Merge manual player if applicable
            if (invite.manual_player_id) {
              await mergeManualPlayer(invite.manual_player_id, user.id);
            }

            // Mark invite as accepted
            await acceptInvite(invite.id);

            // Fetch inviter name for toast
            const { data: inviterProfile } = await supabase
              .from('profiles')
              .select('name')
              .eq('id', invite.inviter_id)
              .single();

            const inviterName = inviterProfile?.name || 'Someone';
            toast.success(`You're now friends with ${inviterName}!`);
          } catch (inviteError) {
            console.error('Error processing invite:', invite.id, inviteError);
          }
        }
      } catch (error) {
        console.error('Error processing invites:', error);
      }
    };

    processInvites();
  }, [user]);
};
